/**
 * The signed-in session, as context.
 *
 * App.tsx already fetches it once and passes it down as a prop. Deep inside a
 * page — the video player, a canvas overlay — prop-drilling it through six
 * layers is noise, so those read it here instead. This provider takes the
 * session App.tsx ALREADY HAS: it never fetches, so there is exactly one
 * `/api/session` request per load and no window where two copies disagree.
 *
 * `useSession()` throws outside a provider rather than returning null. A page
 * that renders without a session is a routing bug, and a loud one is cheaper
 * than every caller writing `session?.org?.code` forever.
 */
import { createContext, useContext, type ReactNode } from 'react'

import type { Session } from './api'

const SessionContext = createContext<Session | null>(null)

export function SessionProvider({ session, children }: { session: Session; children: ReactNode }) {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (session === null) {
    throw new Error('useSession() outside <SessionProvider>: this page was rendered without a session')
  }
  return session
}
